// services/escoService.js - VERSIÓN CORREGIDA UNIVERSAL

const ESCO_API_BASE_URL = process.env.REACT_APP_ESCO_API_BASE_URL || 'https://ec.europa.eu/esco/api';

class EscoService {
  constructor() {
    // Mapeo de términos comunes a términos que ESCO entiende mejor
    this.searchTermMappings = {
      // Desarrollo de Software
      'Frontend': ['desarrollador web', 'programador frontend', 'desarrollador de aplicaciones web'],
      'Backend': ['desarrollador de software', 'programador backend', 'ingeniero de software'],
      'Full-Stack': ['desarrollador de aplicaciones', 'programador de software', 'desarrollador web'],
      'DevOps': ['administrador de sistemas', 'ingeniero de sistemas', 'técnico en sistemas'],
      'QA / Testing': ['analista de pruebas', 'tester de software', 'analista de calidad'],
      'Analista': ['analista de sistemas', 'analista de datos', 'analista'],
      
      // Otras profesiones comunes
      'Contador': ['contador', 'contable', 'especialista en contabilidad'],
      'Médico': ['médico', 'doctor', 'profesional médico'],
      'Abogado': ['abogado', 'jurista', 'asesor legal'],
      'Ingeniero Civil': ['ingeniero civil', 'ingeniero de construcción'],
      'Arquitecto': ['arquitecto', 'diseñador arquitectónico'],
      'Chef': ['chef', 'cocinero', 'especialista culinario'],
      'Diseñador': ['diseñador gráfico', 'diseñador', 'especialista en diseño'],
      'Marketing': ['especialista en marketing', 'analista de marketing', 'gerente de marketing'],
      'Ventas': ['vendedor', 'representante de ventas', 'especialista en ventas'],
      'Profesor': ['profesor', 'educador', 'docente'],
      'Psicólogo': ['psicólogo', 'especialista en psicología'],
      'Enfermero': ['enfermero', 'especialista en enfermería'],
      'Fisioterapeuta': ['fisioterapeuta', 'terapeuta físico']
    };
  }

  // Obtener términos de búsqueda más efectivos
  getSearchTerms(originalTerm) {
    // Si existe un mapeo específico, usarlo
    if (this.searchTermMappings[originalTerm]) {
      return this.searchTermMappings[originalTerm];
    }
    
    // Si no, usar el término original y algunas variantes
    return [
      originalTerm,
      `especialista en ${originalTerm.toLowerCase()}`,
      `analista ${originalTerm.toLowerCase()}`,
      `técnico ${originalTerm.toLowerCase()}`
    ];
  }

  // Buscar ocupaciones con múltiples términos
  async searchOccupationsMultipleTerms(cargoName) {
    const searchTerms = this.getSearchTerms(cargoName);
    const allResults = [];

    console.log(`🔍 Buscando ocupaciones para "${cargoName}" con términos:`, searchTerms);

    for (const term of searchTerms) {
      try {
        const url = `${ESCO_API_BASE_URL}/search?text=${encodeURIComponent(term)}&type=occupation&language=es&limit=5`;
        console.log(`📡 Consultando ESCO: ${url}`);
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          console.log(`✅ Respuesta ESCO para "${term}":`, data);
          
          if (data._embedded?.results) {
            allResults.push(...data._embedded.results);
          }
        } else {
          console.warn(`⚠️ Error ${response.status} para término "${term}"`);
        }
      } catch (error) {
        console.warn(`⚠️ Error buscando "${term}":`, error.message);
      }
      
      // Pequeña pausa entre requests para evitar rate limiting
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    console.log(`Debug: allResults before unique filtering: ${allResults.length} items`);
    // Remover duplicados por URI (método más robusto)
    const uniqueResults = Array.from(new Map(allResults.map(item => [item.uri, item])).values());

    console.log(`📊 Total ocupaciones únicas encontradas: ${uniqueResults.length}`);
    return uniqueResults;
  }

  // Obtener detalles de ocupación con manejo de errores mejorado
  async getOccupationDetails(occupationUri) {
    try {
      const url = `${ESCO_API_BASE_URL}/resource/occupation?uri=${encodeURIComponent(occupationUri)}&language=es`;
      console.log(`📋 Obteniendo detalles: ${url}`);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log(`📄 Detalles obtenidos:`, data);
      return data;
    } catch (error) {
      console.error('Error obteniendo detalles de ocupación:', error);
      throw error;
    }
  }

  // Extraer habilidades de los datos de ESCO
  extractSkillsFromOccupation(occupationData) {
    const skills = [];

    // Función helper para procesar skills
    const processSkills = (skillsData, type) => {
      if (!skillsData) return;
      
      const skillsArray = Array.isArray(skillsData) ? skillsData : [skillsData];
      skillsArray.forEach(skill => {
        if (skill.title) {
          skills.push({
            title: this.cleanSkillTitle(skill.title),
            type: type,
            uri: skill.href || skill.uri
          });
        }
      });
    };

    // Procesar diferentes tipos de habilidades
    if (occupationData._links) {
      processSkills(occupationData._links.hasEssentialSkill, 'essential');
      processSkills(occupationData._links.hasOptionalSkill, 'optional');
      processSkills(occupationData._links.isEssentialSkillFor, 'essential');
      processSkills(occupationData._links.isOptionalSkillFor, 'optional');
    }

    return skills;
  }

  // Limpiar títulos de habilidades
  cleanSkillTitle(title) {
    if (!title) return '';
    
    return title
      .toLowerCase()
      .replace(/^\w/, c => c.toUpperCase()) // Primera letra mayúscula
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Función principal mejorada para obtener habilidades
  async getSkillsForPosition(cargoName) {
    try {
      console.log(`🎯 Iniciando búsqueda de habilidades para: "${cargoName}"`);
      
      // Paso 1: Buscar ocupaciones con múltiples términos
      const occupations = await this.searchOccupationsMultipleTerms(cargoName);
      
      if (!occupations || occupations.length === 0) {
        console.log(`❌ No se encontraron ocupaciones para "${cargoName}"`);
        return {
          occupation: { title: cargoName, source: 'esco' },
          skills: [],
          message: `No se encontraron ocupaciones relacionadas con "${cargoName}" en ESCO`
        };
      }

      console.log(`✅ Encontradas ${occupations.length} ocupaciones, procesando la más relevante...`);

      // Paso 2: Obtener detalles de la ocupación más relevante
      const bestMatch = occupations[0];
      const details = await this.getOccupationDetails(bestMatch.uri);
      
      // Paso 3: Extraer habilidades
      const skills = this.extractSkillsFromOccupation(details);
      
      console.log(`📋 Habilidades extraídas: ${skills.length}`);

      return {
        occupation: {
          title: bestMatch.title,
          uri: bestMatch.uri,
          source: 'esco'
        },
        skills: skills.slice(0, 20), // Limitar a las 20 más relevantes
        message: `${skills.length} habilidades encontradas para "${bestMatch.title}"`
      };
      
    } catch (error) {
      console.error(`💥 Error general obteniendo habilidades para "${cargoName}":`, error);
      return {
        occupation: { title: cargoName, source: 'esco' },
        skills: [],
        error: error.message,
        message: `Error al consultar ESCO: ${error.message}`
      };
    }
  }

  // Función para buscar habilidades directamente (útil para autocompletado)
  async searchSkills(skillName, limit = 10) {
    try {
      const url = `${ESCO_API_BASE_URL}/search?text=${encodeURIComponent(skillName)}&type=skill&language=es&limit=${limit}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data._embedded?.skills) {
        return data._embedded.skills.map(skill => ({
          title: this.cleanSkillTitle(skill.title),
          uri: skill.uri
        }));
      }

      return [];
    } catch (error) {
      console.error('Error buscando habilidades:', error);
      return [];
    }
  }

  // Función de debug para probar la API
  async debugSearch(term) {
    console.log(`🐛 DEBUG: Probando búsqueda para "${term}"`);
    
    // Probar búsqueda de ocupaciones
    const occupations = await this.searchOccupationsMultipleTerms(term);
    console.log(`🐛 Ocupaciones encontradas:`, occupations);
    
    if (occupations.length > 0) {
      // Probar obtener detalles
      const details = await this.getOccupationDetails(occupations[0].uri);
      console.log(`🐛 Detalles de la primera ocupación:`, details);
      
      // Probar extracción de habilidades
      const skills = this.extractSkillsFromOccupation(details);
      console.log(`🐛 Habilidades extraídas:`, skills);
    }
    
    return { occupations, term };
  }
}

export default new EscoService();